import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "../../shared/ui/button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { useBacktest, useCancelBacktest, useDeleteBacktest } from "./backtest-queries.js";
import { ProblemNotice } from "./problem-notice.js";
import { EvidenceDownload } from "./evidence-download.js";

function money(value: string): string {
  const number = Number(value);
  return Number.isFinite(number)
    ? new Intl.NumberFormat(undefined, { currency: "USD", style: "currency" }).format(number)
    : value || "Unavailable";
}

export function BacktestDetailPage({
  backtestId,
  botId,
}: {
  readonly backtestId: string;
  readonly botId: string;
}) {
  const navigate = useNavigate();
  const backtest = useBacktest(backtestId);
  const cancel = useCancelBacktest(backtestId, botId);
  const deleteBacktest = useDeleteBacktest(botId);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  if (backtest.isLoading)
    return (
      <ResourceState
        kind="loading"
        title="Reconnecting to backtest"
        description="Loading durable run status without launching another run."
      />
    );
  if (backtest.error !== null)
    return (
      <ResourceState
        kind="error"
        title="Backtest unavailable"
        description="TradingEngine could not return this run."
        action={
          <Link className="button button--quiet" to={`/bots/${botId}`}>
            Return to Bot
          </Link>
        }
      />
    );
  if (backtest.data === undefined)
    return (
      <ResourceState
        kind="not-found"
        title="Backtest not found"
        description="This run does not exist or is not accessible."
      />
    );
  const run = backtest.data;
  const dxlink = run.versions.marketDataVersion.startsWith("dxlink-candles-v1:");
  const active = run.status === "queued" || run.status === "running";
  return (
    <section className="page-stack" aria-labelledby="backtest-title">
      <header className="page-header page-header--action">
        <div>
          <p className="eyebrow">Frozen revision {run.revisionNumber || "—"}</p>
          <h1 id="backtest-title">{run.request.symbol || "Backtest"}</h1>
          <p>
            Run {run.id} · Bot run {run.botRunId || "unavailable"}
          </p>
        </div>
        <span className={`status-pill status-pill--${run.status}`}>{run.status}</span>
      </header>
      <div className="content-card section-stack backtest-zone">
        <div className="connection-heading">
          <div>
            <h2>Run progress</h2>
            <p className="section-description">
              This page reconnects to the existing run and refreshes while work is active.
            </p>
          </div>
          <div className="button-row">
            <Link className="button button--quiet" to={`/bots/${botId}`}>
              Return to Bot
            </Link>
            {run.status === "completed" && run.request.symbol !== "" ? (
              <Link
                className="button"
                to={`/tickers/${encodeURIComponent(run.request.symbol)}?view=backtesting&backtestId=${encodeURIComponent(run.id)}`}
              >
                View trade chart
              </Link>
            ) : null}
            {active ? (
              <Button
                disabled={cancel.isPending}
                onClick={() => void cancel.mutateAsync()}
                tone="quiet"
              >
                {cancel.isPending ? "Cancelling…" : "Cancel run"}
              </Button>
            ) : null}
            {!active && !confirmingDelete ? (
              <Button
                disabled={deleteBacktest.isPending}
                onClick={() => {
                  deleteBacktest.reset();
                  setConfirmingDelete(true);
                }}
                tone="danger"
              >
                Delete result
              </Button>
            ) : null}
          </div>
        </div>
        {confirmingDelete ? (
          <div
            aria-label={`Confirm deletion of backtest ${run.id}`}
            className="delete-confirmation"
            role="alertdialog"
          >
            <div>
              <strong>Delete this backtest result permanently?</strong>
              <p>The run record and its stored evidence cannot be recovered.</p>
            </div>
            <div className="button-row">
              <Button
                disabled={deleteBacktest.isPending}
                onClick={() => setConfirmingDelete(false)}
                tone="quiet"
              >
                Keep result
              </Button>
              <Button
                disabled={deleteBacktest.isPending}
                onClick={() =>
                  deleteBacktest.mutate(run.id, {
                    onSuccess: () => void navigate(`/bots/${botId}`, { replace: true }),
                  })
                }
                tone="danger"
              >
                {deleteBacktest.isPending ? "Deleting…" : "Delete permanently"}
              </Button>
            </div>
          </div>
        ) : null}
        <div className="progress-stack">
          <progress max="100" value={run.progressPercent}>
            {run.progressPercent}%
          </progress>
          <span>
            {run.progressPercent}% complete · updated{" "}
            {run.updatedAt === "" ? "unknown" : new Date(run.updatedAt).toLocaleString()}
          </span>
        </div>
        {run.status === "cancelled" ? (
          <ResourceState
            kind="stale"
            title="Backtest cancelled"
            description="Partial work was discarded; the frozen revision remains unchanged."
          />
        ) : null}
        {run.status === "failed" ? (
          <ResourceState
            kind="error"
            title="Backtest failed"
            description={run.failure ?? "TradingEngine did not provide failure details."}
          />
        ) : null}
        {cancel.error === null ? null : <ProblemNotice error={cancel.error} />}
        {deleteBacktest.error === null ? null : <ProblemNotice error={deleteBacktest.error} />}
      </div>

      {run.summary === undefined ? null : (
        <div className="content-card section-stack">
          <h2>Summary</h2>
          <dl className="metric-grid">
            <div>
              <dt>Final equity</dt>
              <dd>{money(run.summary.finalEquityUsd)}</dd>
            </div>
            <div>
              <dt>Realized result</dt>
              <dd>{money(run.summary.realizedPnlUsd)}</dd>
            </div>
            <div>
              <dt>Total fees</dt>
              <dd>{money(run.summary.totalFeesUsd)}</dd>
            </div>
            <div>
              <dt>Fills</dt>
              <dd>{run.summary.fillCount}</dd>
            </div>
            <div>
              <dt>Bars</dt>
              <dd>{run.summary.barCount}</dd>
            </div>
            <div>
              <dt>Initial cash</dt>
              <dd>{money(run.summary.initialCashUsd)}</dd>
            </div>
          </dl>
        </div>
      )}

      <div className="detail-grid">
        <div className="content-card section-stack">
          <h2>Frozen inputs</h2>
          <dl className="definition-grid">
            <div>
              <dt>Revision</dt>
              <dd>{run.revisionId || "Unavailable"}</dd>
            </div>
            <div>
              <dt>Date range</dt>
              <dd>
                {run.request.from || "—"} to {run.request.to || "—"}
              </dd>
            </div>
            <div>
              <dt>Bars</dt>
              <dd>
                {run.request.interval || "—"} ·{" "}
                {dxlink ? "Native DXLink candles" : run.request.adjustment || "—"}
              </dd>
            </div>
            <div>
              <dt>Fill model</dt>
              <dd>{run.request.fillTiming || "—"}</dd>
            </div>
            <div>
              <dt>Slippage</dt>
              <dd>{run.request.slippageBasisPoints} bps</dd>
            </div>
            <div>
              <dt>Commission/share</dt>
              <dd>{money(run.request.commissionPerShareUsd)}</dd>
            </div>
            <div>
              <dt>Minimum commission per order</dt>
              <dd>{money(run.request.minimumCommissionUsd ?? "0")}</dd>
            </div>
          </dl>
        </div>
        <div className="content-card section-stack">
          <h2>Provenance</h2>
          <dl className="definition-grid">
            <div>
              <dt>Data provider</dt>
              <dd>
                {dxlink
                  ? "tastytrade DXLink"
                  : run.versions.marketDataVersion.startsWith("stocks-v2-bars-v1:")
                    ? "Alpaca"
                    : "Unavailable"}
              </dd>
            </div>
            <div>
              <dt>Engine</dt>
              <dd>{run.versions.engineVersion || "Unavailable"}</dd>
            </div>
            <div>
              <dt>Strategy runtime</dt>
              <dd>{run.versions.strategyRuntimeVersion || "Unavailable"}</dd>
            </div>
            <div>
              <dt>Market data</dt>
              <dd>{run.versions.marketDataVersion || "Unavailable"}</dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="content-card section-stack">
        <h2>Detailed evidence</h2>
        {run.status === "completed" && run.request.symbol !== "" ? (
          <Link
            className="button"
            to={`/tickers/${encodeURIComponent(run.request.symbol)}?view=backtesting&backtestId=${encodeURIComponent(run.id)}`}
          >
            Open candles, executions, decisions, and equity curve
          </Link>
        ) : null}
        {run.status === "completed" && run.artifactKey !== undefined ? (
          <EvidenceDownload key={run.id} run={run} />
        ) : (
          <ResourceState
            kind="degraded"
            title="Evidence not yet available"
            description="TradingEngine has not published a completed result artifact for this run."
          />
        )}
        <p className="section-description">
          The symbol workspace renders the authenticated run artifact without exposing the private
          storage bucket.
        </p>
      </div>
    </section>
  );
}
